import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useContext, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { SnackBarType } from '../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../layout/SnackbarContext';
import {
  DigitalOwlJob,
  DigitalOwlUploadStatus,
  downloadDigitalOwlResult,
  getDigitalOwlUploads,
  uploadDigitalOwlCsv,
} from '../digital-owl/api';

const UPLOADS_QUERY_KEY = ['digital-owl-uploads'];

const STATUS_CHIP_PROPS: Record<
  DigitalOwlUploadStatus,
  { label: string; color: 'default' | 'primary' | 'info' | 'success' | 'error' }
> = {
  [DigitalOwlUploadStatus.PENDING]: { label: 'Pending', color: 'default' },
  [DigitalOwlUploadStatus.PROCESSING]: {
    label: 'Processing…',
    color: 'primary',
  },
  [DigitalOwlUploadStatus.COMPLETED]: { label: 'Completed', color: 'success' },
  [DigitalOwlUploadStatus.FAILED]: { label: 'Failed', color: 'error' },
};

function isInProgress(status: DigitalOwlUploadStatus): boolean {
  return (
    status === DigitalOwlUploadStatus.PENDING ||
    status === DigitalOwlUploadStatus.PROCESSING
  );
}

export function DigitalOwlPanel() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const queryClient = useQueryClient();

  const { data: uploadsData } = useQuery<DigitalOwlJob[]>({
    queryKey: UPLOADS_QUERY_KEY,
    queryFn: () => getDigitalOwlUploads(10),
    refetchInterval: data => {
      const latest = Array.isArray(data) ? data[0] : undefined;
      return latest && isInProgress(latest.status) ? 3000 : false;
    },
  });
  const uploads = Array.isArray(uploadsData) ? uploadsData : [];

  const latestJob = uploads[0];

  const { mutate: uploadFile, isLoading: uploading } = useMutation({
    mutationFn: async (files: File[]) => uploadDigitalOwlCsv(files[0]),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UPLOADS_QUERY_KEY });
      setSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.SUCCESS,
          message: 'DigitalOwl CSV upload started',
        },
      ]);
    },
    onError: (error: any) => {
      setSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message: error?.message || error || 'Failed to upload DigitalOwl CSV',
        },
      ]);
    },
  });

  const handleUploadButtonClick = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) {
      uploadFile(Array.from(files));
      e.target.value = '';
    }
  };

  const renderStatusChip = (job: DigitalOwlJob) => {
    const chip = STATUS_CHIP_PROPS[job.status] ?? {
      label: job.status,
      color: 'default' as const,
    };
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label={chip.label} color={chip.color as any} size="small" />
        {isInProgress(job.status) && <CircularProgress size={14} />}
      </Box>
    );
  };

  const isProcessingLatest = latestJob && isInProgress(latestJob.status);
  const uploadDisabled = uploading || !!isProcessingLatest;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4,
        }}
      >
        <Typography variant="h1">DigitalOwl Upload</Typography>

        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            size="medium"
            onClick={handleUploadButtonClick}
            disabled={uploadDisabled}
          >
            {uploading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Uploading…
              </Box>
            ) : isProcessingLatest ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Processing…
              </Box>
            ) : (
              'Upload DigitalOwl CSV'
            )}
          </Button>
        </Box>
      </Box>

      {uploads.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No uploads yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell>Uploaded by</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Policies</TableCell>
                <TableCell>Processed</TableCell>
                <TableCell>Skipped</TableCell>
                <TableCell>Not found</TableCell>
                <TableCell>Errors</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {uploads.map(job => (
                <TableRow key={job.id}>
                  <TableCell sx={{ wordBreak: 'break-all' }}>
                    {job.originalFilename}
                  </TableCell>
                  <TableCell>{job.uploadedBy ?? '—'}</TableCell>
                  <TableCell>
                    {new Date(job.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>{renderStatusChip(job)}</TableCell>
                  <TableCell>
                    {job.resultSummary?.totalPolicies ?? '—'}
                  </TableCell>
                  <TableCell>
                    {job.resultSummary?.processedCount ?? '—'}
                  </TableCell>
                  <TableCell>
                    {job.resultSummary?.skippedCount ?? '—'}
                  </TableCell>
                  <TableCell>
                    {job.resultSummary?.notFoundCount ?? '—'}
                  </TableCell>
                  <TableCell>{job.resultSummary?.errorCount ?? '—'}</TableCell>
                  <TableCell>
                    {job.status === DigitalOwlUploadStatus.COMPLETED &&
                      job.resultSummary?.resultS3Key && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => void downloadDigitalOwlResult(job.id)}
                        >
                          Download
                        </Button>
                      )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
